import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { FileSignature, Plus, Send, CheckCircle2, Archive, Eye, Trash2, RefreshCw, FileText,
         Copy, Sparkles, Loader2, ArrowLeft, ArrowRight, FolderOpen, AlertCircle, Mail, Unlink, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TENANT_LANGUAGES } from "@/lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  envoye: { label: "En attente de signature", cls: "bg-blue-50 text-blue-700 border-blue-200", step: 3 },
  signe_recu: { label: "Signé reçu", cls: "bg-amber-50 text-amber-700 border-amber-200", step: 4 },
  verifie: { label: "Vérifié", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", step: 5 },
  classe: { label: "Signé & classé", cls: "bg-slate-900 text-white border-slate-900", step: 6 },
};
const PIPELINE = ["brouillon", "pret", "envoye", "signe_recu", "verifie", "classe"];

const MOTIF_OPTIONS = [
  ["formation", "Formation"], ["etudes", "Études"], ["stage", "Stage"],
  ["apprentissage", "Apprentissage"], ["mission", "Mission professionnelle"],
  ["mutation", "Mutation"], ["service_civique", "Service civique"], ["autre", "Autre"],
];

const TENANT_MESSAGE = `Bonjour,

Pour établir votre bail mobilité, merci de me transmettre les informations suivantes :
- Votre nom et prénom
- Votre date de naissance et votre lieu de naissance
- Votre adresse e-mail
- Le motif de votre séjour (études, stage, mission professionnelle, mutation, etc.)

Merci d'avance !`;

function leaseComplete(l) {
  return !!(l.tenant_first_name && l.tenant_last_name && l.tenant_email && l.tenant_motif
    && l.monthly_rent != null && l.start_date && l.end_date);
}

function draftStatus(l) {
  return leaseComplete(l)
    ? { label: "Prêt à envoyer", cls: "bg-amber-50 text-amber-700 border-amber-200", step: 2 }
    : { label: "En attente d'informations", cls: "bg-red-50 text-red-700 border-red-200", step: 1 };
}

function statusMeta(l) {
  return l.status === "brouillon" ? draftStatus(l) : STATUS_META[l.status];
}

function Field({ label, ok, required, children }) {
  return (
    <div>
      <Label className="text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <div className={`mt-1 rounded-md transition-shadow ${ok ? "[&>input]:border-emerald-400 [&>button]:border-emerald-400 [&>div]:border-emerald-400" : ""}`}>
        {children}
      </div>
    </div>
  );
}

function LeaseFlow({ lease, apartment, open, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({});
  const [aiText, setAiText] = useState("");
  const [overlap, setOverlap] = useState(null);
  const [overlapChoice, setOverlapChoice] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && lease) {
      setStep(1); setAiText("");
      setF({
        tenant_first_name: lease.tenant_first_name || "", tenant_last_name: lease.tenant_last_name || "",
        tenant_email: lease.tenant_email || "", tenant_phone: lease.tenant_phone || "",
        tenant_birth_date: lease.tenant_birth_date || "", tenant_birth_place: lease.tenant_birth_place || "",
        tenant_motif: lease.tenant_motif || "", monthly_rent: lease.monthly_rent ?? "",
        charges: lease.charges ?? "", start_date: lease.start_date || "", end_date: lease.end_date || "",
        tenant_language: lease.tenant_language || "fr",
      });
    }
  }, [open, lease?.id]);

  if (!lease || !apartment) return null;
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const filled = (v) => v !== "" && v != null;

  const save = async (patch = {}) => {
    const payload = { ...f, ...patch };
    if (payload.tenant_birth_date && !birthOk(payload.tenant_birth_date)) {
      toast.error("Date de naissance invalide — le locataire doit avoir au moins 18 ans (format JJ/MM/AAAA)");
      return false;
    }
    if (payload.monthly_rent !== "") payload.monthly_rent = parseFloat(payload.monthly_rent);
    else payload.monthly_rent = null;
    if (payload.charges !== "") payload.charges = parseFloat(payload.charges);
    else payload.charges = null;
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    try {
      await api.put(`/leases/${lease.id}`, payload);
      onSaved();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de sauvegarde"); return false; }
  };

  const birthOk = (v) => {
    if (!v) return true;
    const t = v.trim();
    let y, mo, d;
    const fr = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (fr) { [, d, mo, y] = fr; }
    else if (iso) { [, y, mo, d] = iso; }
    else return false;
    const dob = new Date(+y, +mo - 1, +d);
    if (isNaN(dob.getTime())) return false;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    if (now.getMonth() < dob.getMonth() ||
        (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age -= 1;
    return age >= 18;
  };

  const resetForm = () => {
    setF({
      tenant_first_name: "", tenant_last_name: "", tenant_email: "", tenant_phone: "",
      tenant_birth_date: "", tenant_birth_place: "", tenant_motif: "", monthly_rent: "",
      charges: "", start_date: "", end_date: "", tenant_language: f.tenant_language || "fr",
    });
    setAiText("");
    toast.success("Informations remises à zéro — vous pouvez relancer une analyse");
  };

  const parseAi = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/leases/parse-tenant", { text: aiText });
      const g = data.fields || {};
      set({
        tenant_first_name: g.first_name || f.tenant_first_name,
        tenant_last_name: g.last_name || f.tenant_last_name,
        tenant_email: g.email || f.tenant_email,
        tenant_birth_date: g.birth_date || f.tenant_birth_date,
        tenant_birth_place: g.birth_place || f.tenant_birth_place,
        tenant_motif: g.motif || f.tenant_motif,
        monthly_rent: g.monthly_rent != null ? String(g.monthly_rent) : f.monthly_rent,
        charges: g.charges != null ? String(g.charges) : f.charges,
        start_date: g.start_date || f.start_date,
        end_date: g.end_date || f.end_date,
        tenant_language: g.language || f.tenant_language,
      });
      toast.success("Informations extraites — vérifiez les champs ci-dessous");
    } catch { toast.error("Analyse impossible — remplissez manuellement"); }
    finally { setAiLoading(false); }
  };

  const complete = ["tenant_first_name", "tenant_last_name", "tenant_email", "tenant_motif", "start_date", "end_date"].every((k) => filled(f[k])) && filled(f.monthly_rent);

  const overlapInfo = overlap
    ? { days: Math.round((new Date(f.start_date) - new Date(overlap.start_date)) / 86400000), newStart: f.start_date }
    : null;

  const isNextOverlap = !!(overlap && f.start_date && new Date(overlap.start_date) >= new Date(f.start_date));

  const resolveOverlap = async (forcedAction) => {
    const action = forcedAction || overlapChoice;
    setBusy(true);
    try {
      const { data: ro } = await api.post(`/leases/${lease.id}/resolve-overlap`, {
        prev_lease_id: overlap.lease_id,
        action,
      });
      if (ro?.drive_updated === false) {
        toast.warning("Conflit résolu, mais la mise à jour du fichier Drive a échoué — vérifiez le dossier manuellement.");
      } else {
        toast.success(action === "annule"
          ? (isNextOverlap ? "Contrat suivant supprimé (Drive inclus)" : "Ancien contrat supprimé (Drive inclus)")
          : "Contrat précédent raccourci — fichier modifié et remplacé dans Drive (signature conservée)");
      }
      setOverlap(null); setOverlapChoice(null);
      await generateAndSend();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const generateAndSend = async () => {
    setBusy(true);
    try {
      if (!(await save())) return;
      await api.post(`/leases/${lease.id}/generate`);
      await api.post(`/leases/${lease.id}/send`);
      toast.success(`Bail envoyé à ${f.tenant_email}`);
      onClose(); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "overlap") {
        setOverlap(d.conflict);
      } else {
        toast.error(typeof d === "string" ? d : "Échec de l'envoi");
      }
    }
    finally { setBusy(false); }
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(TENANT_MESSAGE).then(() => toast.success("Message copié — collez-le au voyageur"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="lease-flow" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="truncate">{apartment.name} — Bail mobilité</span>
            <span className="text-sm font-normal text-slate-500 flex-shrink-0 ml-3" data-testid="lf-step">Étape {step} sur 3</span>
          </DialogTitle>
        </DialogHeader>
        {overlap && (
          <div className="space-y-4 py-2" data-testid="overlap-panel">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-2 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  Ce contrat couvre la période du contrat précédent (<strong>{overlap.tenant_name || "locataire précédent"}</strong>,
                  séjour du {overlap.start_date} au {overlap.end_date} — {overlap.duration_days} jours).
                  Que s'est-il passé pour cette réservation ?
                </div>
              </div>
            </div>
            {isNextOverlap ? (
              <div className="space-y-3" data-testid="overlap-next-panel">
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
                  Le nouveau contrat dépasse le début du contrat suivant (du {overlap.start_date} au {overlap.end_date}).
                  Celui-ci n'est plus viable : <strong>il doit être supprimé</strong>. Son document signé sera également
                  supprimé de Drive s'il existe.
                </p>
                <Button onClick={() => resolveOverlap("annule")} disabled={busy} data-testid="overlap-next-delete"
                        className="bg-red-600 hover:bg-red-700 text-white">
                  {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Supprimer le contrat suivant et envoyer
                </Button>
              </div>
            ) : !overlapChoice ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOverlapChoice("annule")} data-testid="overlap-choice-annule"
                          className="border-red-300 text-red-700 hover:bg-red-50">La réservation a été annulée</Button>
                  <Button variant="outline" onClick={() => setOverlapChoice("raccourci")} data-testid="overlap-choice-raccourci"
                          disabled={overlapInfo && overlapInfo.days < 30}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50">La réservation a été raccourcie</Button>
                </div>
                {overlapInfo && overlapInfo.days < 30 && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="overlap-nonlegal-warning">
                    Le nouveau contrat réduirait le précédent à {overlapInfo.days} jour{overlapInfo.days > 1 ? "s" : ""} — sous la durée
                    légale minimale de 30 jours. Le contrat précédent n'est donc pas modifiable : il doit être supprimé
                    (choisissez « La réservation a été annulée »).
                  </p>
                )}
              </div>
            ) : overlapChoice === "annule" ? (
              <div className="space-y-3">
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="overlap-annule-warning">
                  Attention : cette action supprimera définitivement l'ancien contrat signé, y compris dans le dossier Drive.
                </p>
                <div className="flex gap-2">
                  <Button onClick={resolveOverlap} disabled={busy} data-testid="overlap-confirm"
                          className="bg-red-600 hover:bg-red-700 text-white">
                    {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Confirmer la suppression
                  </Button>
                  <Button variant="ghost" onClick={() => setOverlapChoice(null)} data-testid="overlap-back">Retour</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Le contrat précédent sera automatiquement raccourci au <strong>{overlapInfo?.newStart}</strong> (début du
                  nouveau contrat), soit <strong>{overlapInfo?.days} jours</strong> — la durée légale minimale de 30 jours est respectée.
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  Le contrat signé sera <strong>modifié dans son contenu</strong> (nouvelle date de fin), la signature du locataire
                  sera conservée, puis le fichier remplacera l'ancien dans Drive. Le nouveau contrat portera la mention « séjour raccourci ».
                </p>
                <div className="flex gap-2">
                  <Button onClick={resolveOverlap} disabled={busy} data-testid="overlap-confirm"
                          className="bg-blue-600 hover:bg-blue-700 text-white">
                    {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Valider et envoyer
                  </Button>
                  <Button variant="ghost" onClick={() => setOverlapChoice(null)} data-testid="overlap-back">Retour</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-2">
          <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${(step / 3) * 100}%` }} />
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll pr-1">
          {step === 1 && (
            <div className="space-y-3">
              {!apartment.drive_folder_id && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="lf-drive-warning">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  ID Drive en attente : renseignez l'ID du dossier Drive de cet appartement via « Modifier » sur sa fiche avant tout envoi.
                </div>
              )}
              <p className="text-sm text-slate-600">
                Copiez ce message et envoyez-le au voyageur (SMS, WhatsApp, Airbnb…) pour recueillir ses informations :
              </p>
              <Textarea readOnly value={TENANT_MESSAGE} rows={9} className="bg-slate-50 text-sm" data-testid="lf-message" />
              <Button variant="outline" onClick={copyMessage} data-testid="lf-copy" className="w-full">
                <Copy className="w-4 h-4 mr-1.5" />Copier le message
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="border border-sky-200 bg-sky-50/60 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Sparkles className="w-4 h-4 text-sky-600" /> Remplissage intelligent
                </div>
                <Textarea value={aiText} onChange={(e) => setAiText(e.target.value)} rows={3}
                          placeholder={'Collez ici la réponse du client, ex : « bonjour voici les informations demandées : Léa Martin, née le 12/04/2001 à Bordeaux, lea.martin@email.fr, je viens pour mon stage, loyer convenu 1200 € avec 100 € de charges, du 01/11/2026 au 30/04/2027 »'}
                          data-testid="lf-ai-text" className="bg-white text-sm" />
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={parseAi} disabled={aiLoading || !aiText.trim()}
                          data-testid="lf-ai-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                    {aiLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    Analyser et répartir
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={resetForm}
                          data-testid="lf-ai-reset" title="Remet tous les champs à zéro avant une nouvelle analyse">
                    Réinitialiser
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Prénom" ok={filled(f.tenant_first_name)} required>
                  <Input value={f.tenant_first_name} onChange={(e) => set({ tenant_first_name: e.target.value })} data-testid="lf-first-name" />
                </Field>
                <Field label="Nom" ok={filled(f.tenant_last_name)} required>
                  <Input value={f.tenant_last_name} onChange={(e) => set({ tenant_last_name: e.target.value })} data-testid="lf-last-name" />
                </Field>
              </div>
              <Field label="Email du locataire" ok={filled(f.tenant_email)} required>
                <Input type="email" value={f.tenant_email} onChange={(e) => set({ tenant_email: e.target.value })} data-testid="lf-email" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date de naissance (optionnel)" ok={filled(f.tenant_birth_date)}>
                  <Input value={f.tenant_birth_date} onChange={(e) => set({ tenant_birth_date: e.target.value })} placeholder="JJ/MM/AAAA" data-testid="lf-birth-date" />
                </Field>
                <Field label="Lieu de naissance (optionnel)" ok={filled(f.tenant_birth_place)}>
                  <Input value={f.tenant_birth_place} onChange={(e) => set({ tenant_birth_place: e.target.value })} data-testid="lf-birth-place" />
                </Field>
              </div>
              <Field label="Motif du séjour" ok={filled(f.tenant_motif)} required>
                <Select value={f.tenant_motif} onValueChange={(v) => set({ tenant_motif: v })}>
                  <SelectTrigger data-testid="lf-motif"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{MOTIF_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Langue du locataire (emails)" ok={filled(f.tenant_language)}>
                <Select value={f.tenant_language} onValueChange={(v) => set({ tenant_language: v })}>
                  <SelectTrigger data-testid="lf-language"><SelectValue /></SelectTrigger>
                  <SelectContent>{TENANT_LANGUAGES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Les emails (envoi, relances, rappels) seront traduits dans cette langue. Le contrat reste en français.</p>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Loyer mensuel (€)" ok={filled(f.monthly_rent)} required>
                  <Input type="number" value={f.monthly_rent} onChange={(e) => set({ monthly_rent: e.target.value })} data-testid="lf-rent" />
                </Field>
                <Field label="Charges forfaitaires (€)" ok={filled(f.charges)}>
                  <Input type="number" value={f.charges} onChange={(e) => set({ charges: e.target.value })} data-testid="lf-charges" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Début de la réservation" ok={filled(f.start_date)} required>
                  <Input type="date" value={f.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="lf-start" />
                </Field>
                <Field label="Fin de la réservation" ok={filled(f.end_date)} required>
                  <Input type="date" value={f.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="lf-end" />
                </Field>
              </div>
              {!complete && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="lf-incomplete">
                  Tous les champs marqués * doivent être remplis avant l'envoi. Seuls la date et le lieu de naissance sont optionnels.
                </p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-sm" data-testid="lf-recap">
                {[
                  ["Appartement", apartment.name],
                  ["Locataire", `${f.tenant_first_name} ${f.tenant_last_name}`],
                  ["Email", f.tenant_email],
                  ["Naissance", [f.tenant_birth_date, f.tenant_birth_place].filter(Boolean).join(" à ") || "—"],
                  ["Motif", MOTIF_OPTIONS.find(([k]) => k === f.tenant_motif)?.[1] || "—"],
                  ["Langue des emails", TENANT_LANGUAGES.find(([k]) => k === f.tenant_language)?.[1] || "Français"],
                  ["Période", `${f.start_date} → ${f.end_date}`],
                  ["Loyer", f.monthly_rent ? `${f.monthly_rent} €` : "—"],
                  ["Charges", f.charges ? `${f.charges} €` : "—"],
                  ["Total mensuel", f.monthly_rent ? `${(parseFloat(f.monthly_rent) || 0) + (parseFloat(f.charges) || 0)} €` : "—"],
                  ["Bailleur", apartment.owner_name || "—"],
                  ["Dossier Drive", apartment.drive_folder_id || "Non renseigné"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="text-slate-500">{k}</span>
                    <span className="text-slate-900 font-medium text-right">{v}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Le contrat sera généré en français depuis le modèle « Bail mobilité » avec les cases cochées automatiquement
                (motif du séjour, Wi-Fi, climatisation…), puis envoyé par email au locataire — dans la langue choisie —
                avec son lien de dépôt.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-between pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={() => (step === 1 ? onClose() : setStep(step - 1))} data-testid="lf-prev">
            <ArrowLeft className="w-4 h-4 mr-1.5" />{step === 1 ? "Fermer" : "Précédent"}
          </Button>
          {step === 1 && (
            <Button onClick={() => setStep(2)} data-testid="lf-next-1" className="bg-blue-600 hover:bg-blue-700 text-white">
              Suivant<ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={async () => { if (await save()) setStep(3); }} disabled={!complete} data-testid="lf-next-2"
                    className="bg-blue-600 hover:bg-blue-700 text-white">
              Suivant<ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
          {!overlap && step === 3 && (
            <Button onClick={generateAndSend} disabled={busy} data-testid="lf-send"
                    className="bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Envoyer le bail
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FiledList({ leases }) {
  const groups = {};
  for (const l of leases) {
    const d = new Date(l.filed_at || l.received_at || l.updated_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    (groups[key] = groups[key] || []).push(l);
  }
  const months = Object.keys(groups).sort().reverse();
  const monthLabel = (k) => {
    const [y, m] = k.split("-");
    const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };
  return (
    <div className="space-y-6" data-testid="filed-list">
      {months.map((k) => (
        <div key={k}>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm font-semibold text-slate-900" data-testid={`filed-month-${k}`}>{monthLabel(k)}</span>
            <span className="text-xs text-slate-400">{groups[k].length} bail{groups[k].length > 1 ? "aux" : ""}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {groups[k].map((l) => (
              <div key={l.id} className="flex items-center gap-4 px-4 py-2.5 text-sm flex-wrap" data-testid={`filed-row-${l.id}`}>
                <span className="font-medium text-slate-900 min-w-32 truncate">{l.apartment?.name || "—"}</span>
                <span className="text-slate-600 flex-1 min-w-32 truncate">{l.tenant_name || "—"}</span>
                <span className="text-slate-400 text-xs">{l.start_date} → {l.end_date}</span>
                <span className="text-slate-600 text-xs font-medium">
                  {l.monthly_rent != null ? `${(l.monthly_rent || 0) + (l.charges || 0)} €/mois` : "—"}
                </span>
                <button type="button" onClick={async () => {
                    try {
                      const res = await api.get(`/leases/${l.id}/file/signe`, { responseType: "blob" });
                      window.open(URL.createObjectURL(res.data), "_blank");
                    } catch { toast.error("Impossible d'ouvrir le document"); }
                  }}
                   className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                   data-testid={`filed-link-${l.id}`}>
                  <Eye className="w-3.5 h-3.5" />Bail signé
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeaseCard({ lease, onRefresh, canEdit, onResume }) {
  const st = statusMeta(lease);
  const [sending, setSending] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [rejectMotif, setRejectMotif] = useState("");
  const [rejectCustom, setRejectCustom] = useState("");
  const finalMotif = rejectMotif === "Autre" ? rejectCustom.trim() : rejectMotif;

  const remind = async () => {
    setSending(true);
    try {
      await api.post(`/leases/${lease.id}/remind`);
      toast.success("Rappel envoyé au locataire");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setSending(false); }
  };

  const doReject = async () => {
    setSending(true);
    try {
      await api.post(`/leases/${lease.id}/reject`, { motif: finalMotif });
      toast.success("Bail renvoyé au locataire avec le motif");
      setShowReject(false);
      setRejectMotif("");
      setRejectCustom("");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec du renvoi"); }
    finally { setSending(false); }
  };
  const setStatus = async (status) => {
    try {
      await api.put(`/leases/${lease.id}`, { status });
      toast.success(status === "classe" ? "Bail classé" : "Statut mis à jour");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const doRemove = async () => {
    try {
      const { data } = await api.delete(`/leases/${lease.id}`);
      if (data?.drive_deleted === false) {
        toast.warning("Bail supprimé, mais le fichier Drive n'a pas pu être effacé — supprimez-le manuellement.");
      } else {
        toast.success(lease.status === "brouillon" ? "Bail supprimé" : "Bail annulé");
      }
      onRefresh();
    } catch { toast.error("Erreur"); }
  };
  const remove = () => {
    if (lease.status === "classe") { setConfirmCancel(true); return; }
    const msg = lease.status === "envoye"
      ? "Annuler ce bail en attente de signature ? Le lien de dépôt envoyé au locataire sera immédiatement désactivé."
      : "Supprimer ce dossier de bail ?";
    if (!window.confirm(msg)) return;
    doRemove();
  };
  const viewFile = async (kind) => {
    try {
      const res = await api.get(`/leases/${lease.id}/file/${kind}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Impossible d'ouvrir le document"); }
  };

  return (
    <div className={`bg-white rounded-2xl border p-4 shadow-sm ${lease.status === "brouillon" && !leaseComplete(lease) ? "border-red-300" : "border-slate-200"}`}
         data-testid={`lease-card-${lease.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{lease.apartment?.name || "—"}</div>
          <div className="text-xs text-slate-500 truncate">
            {lease.tenant_name || "Locataire à renseigner"}{lease.tenant_email ? ` · ${lease.tenant_email}` : ""}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Créé le {new Date(lease.created_at).toLocaleDateString("fr-FR")} par {lease.created_by}
          </div>
        </div>
        <Badge className={`${st.cls} border flex-shrink-0`} data-testid={`lease-status-${lease.id}`}>{st.label}</Badge>
      </div>
      {(lease.start_date || lease.end_date) && (
        <div className="text-xs text-slate-500 mb-2">
          {lease.start_date || "?"} → {lease.end_date || "?"}
          {lease.sent_at && <span> · Envoyé le {new Date(lease.sent_at).toLocaleDateString("fr-FR")}</span>}
          {lease.reminded_at && <span> · Relancé le {new Date(lease.reminded_at).toLocaleDateString("fr-FR")}</span>}
          {lease.received_at && <span> · Reçu le {new Date(lease.received_at).toLocaleDateString("fr-FR")}</span>}
        </div>
      )}
      <div className="flex items-center gap-1 mb-3">
        {PIPELINE.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${i < st.step ? "bg-blue-600" : "bg-slate-100"}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {canEdit && lease.status === "brouillon" && (
          <>
            <Button size="sm" onClick={() => onResume(lease)} data-testid={`lease-resume-${lease.id}`}
                    className="bg-blue-600 hover:bg-blue-700 text-white">
              <ArrowRight className="w-3.5 h-3.5 mr-1" />Reprendre le dossier
            </Button>
            <Button size="sm" variant="ghost" onClick={remove} data-testid={`lease-delete-${lease.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </Button>
          </>
        )}
        {canEdit && lease.status === "envoye" && (
          <>
            <Button size="sm" variant="outline" onClick={remind} disabled={sending} data-testid={`lease-remind-${lease.id}`}>
              {sending ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Relancer le locataire
            </Button>
            <Button size="sm" variant="ghost" onClick={remove} data-testid={`lease-cancel-${lease.id}`}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5 mr-1" />Annuler le bail
            </Button>
          </>
        )}
        {(lease.signed_path || lease.signed_drive_link) && (
          <Button size="sm" variant="outline" onClick={() => viewFile("signe")} data-testid={`lease-view-signed-${lease.id}`}>
            <Eye className="w-3.5 h-3.5 mr-1" />Voir le bail signé
          </Button>
        )}
        {lease.contract_path && (
          <Button size="sm" variant="ghost" onClick={() => viewFile("contrat")} data-testid={`lease-view-contract-${lease.id}`}>
            <FileText className="w-3.5 h-3.5 mr-1" />Contrat
          </Button>
        )}
        {canEdit && lease.status === "signe_recu" && (
          <>
            <Button size="sm" onClick={() => setStatus("verifie")} data-testid={`lease-verify-${lease.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Marquer vérifié
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowReject(!showReject)}
                    data-testid={`lease-return-${lease.id}`}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Undo2 className="w-3.5 h-3.5 mr-1" />Renvoyer
            </Button>
            {showReject && (
              <div className="w-full mt-1 bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2"
                   data-testid={`lease-reject-panel-${lease.id}`}>
                <div className="text-xs font-medium text-amber-800">Motif du renvoi (le locataire le recevra par email) :</div>
                <Select value={rejectMotif} onValueChange={setRejectMotif}>
                  <SelectTrigger className="h-8 text-xs bg-white" data-testid={`lease-reject-motif-${lease.id}`}>
                    <SelectValue placeholder="Choisir un motif…" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Document illisible", "Bail non signé", "Informations manquantes ou incorrectes", "Autre"].map((m) => (
                      <SelectItem key={m} value={m} data-testid={`lease-reject-opt-${m}`}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rejectMotif === "Autre" && (
                  <Input value={rejectCustom} onChange={(e) => setRejectCustom(e.target.value)}
                         placeholder="Précisez le motif…" className="h-8 text-xs bg-white"
                         data-testid={`lease-reject-custom-${lease.id}`} />
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={doReject} disabled={!finalMotif || sending}
                          data-testid={`lease-reject-confirm-${lease.id}`}
                          className="bg-amber-600 hover:bg-amber-700 text-white h-8">
                    {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                    Renvoyer au locataire
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setShowReject(false)}
                          data-testid={`lease-reject-cancel-${lease.id}`}>Retour</Button>
                </div>
              </div>
            )}
          </>
        )}
        {canEdit && lease.status === "verifie" && (
          <Button size="sm" onClick={() => setStatus("classe")} data-testid={`lease-file-${lease.id}`}
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            <Archive className="w-3.5 h-3.5 mr-1" />Classer le bail
          </Button>
        )}
      </div>
      {canEdit && lease.status === "classe" && (
        <div className="mt-2">
          <Button size="sm" variant="ghost" onClick={remove} data-testid={`lease-cancel-${lease.id}`}
                  className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-2">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Annuler le bail
          </Button>
        </div>
      )}
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent data-testid={`lease-cancel-dialog-${lease.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler ce bail classé ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le bail sera définitivement retiré de l'historique <strong>et le document signé sera supprimé
              du dossier Google Drive</strong> de l'appartement. Cette procédure est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`lease-cancel-abort-${lease.id}`}>Conserver le bail</AlertDialogCancel>
            <AlertDialogAction onClick={doRemove} data-testid={`lease-cancel-confirm-${lease.id}`}
                               className="bg-red-600 hover:bg-red-700 text-white">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Leases() {
  const { user } = useAuth();
  const [tab, setTab] = useState("consulter");
  const [leases, setLeases] = useState([]);
  const [apts, setApts] = useState([]);
  const [filter, setFilter] = useState("actifs");
  const [flow, setFlow] = useState({ open: false, lease: null, apartment: null });
  const [searchParams, setSearchParams] = useSearchParams();
  const [google, setGoogle] = useState(null);
  const [driveEdit, setDriveEdit] = useState(null);
  const canEdit = ["admin", "account_manager", "assistant_manager"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const loadGoogle = () => api.get("/google/status").then((r) => setGoogle(r.data)).catch(() => {});
  useEffect(() => { loadGoogle(); }, []);

  useEffect(() => {
    const g = searchParams.get("google");
    if (g === "connected") {
      toast.success("Google connecté — les baux partiront de votre Gmail et seront classés dans Drive");
      loadGoogle();
    } else if (g === "error") {
      toast.error("La connexion Google a échoué — réessayez");
    }
    if (g) setSearchParams({}, { replace: true });
  }, [searchParams]);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/google/connect");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Connexion impossible"); }
  };

  const saveDriveInline = async (a) => {
    try {
      await api.put(`/apartments/${a.id}`, { ...a, drive_folder_id: (driveEdit?.value || "").trim() });
      toast.success("ID dossier Drive enregistré");
      setDriveEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Déconnecter Google ? Les emails repasseront par le service par défaut et le classement Drive sera désactivé.")) return;
    try {
      await api.delete("/google/disconnect");
      toast.success("Google déconnecté");
      loadGoogle();
    } catch { toast.error("Erreur"); }
  };

  const load = () => {
    api.get("/leases").then((r) => setLeases(r.data)).catch(() => {});
    api.get("/apartments").then((r) => setApts(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openFlowForApartment = async (apt) => {
    const existing = leases.find((l) => l.apartment_id === apt.id && l.status === "brouillon");
    if (existing) {
      setFlow({ open: true, lease: existing, apartment: apt });
      return;
    }
    try {
      const { data } = await api.post("/leases", { apartment_id: apt.id });
      toast.success("Dossier créé — en attente des informations");
      load();
      setFlow({ open: true, lease: data, apartment: apt });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sorted = [...leases].sort((a, b) => {
    const pa = a.status === "brouillon" ? 0 : 1;
    const pb = b.status === "brouillon" ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return b.created_at.localeCompare(a.created_at);
  });
  const shown = sorted.filter((l) =>
    filter === "actifs" ? l.status !== "classe" : filter === "classes" ? l.status === "classe" : true);
  const mobilityApts = apts.filter((a) => a.mobility_lease);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900 flex items-center gap-2">
          <FileSignature className="w-6 h-6 text-blue-600" />Gestion des BM
        </h1>
        <p className="text-sm text-slate-500 mt-1">Baux mobilité : préparation, envoi, réception, contrôle et classement</p>
      </div>

      {google && (
        <div className={`mb-6 rounded-xl border p-3.5 flex items-center gap-3 text-sm ${
          google.connected ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}
             data-testid="google-status-card">
          <Mail className={`w-4 h-4 flex-shrink-0 ${google.connected ? "text-emerald-600" : "text-slate-400"}`} />
          {google.connected ? (
            <span className="text-emerald-800" data-testid="google-status-text">
              Google connecté (<strong>{google.email}</strong>) — envoi des baux via Gmail et classement Drive actifs
            </span>
          ) : (
            <span className="text-slate-600" data-testid="google-status-text">
              Google non connecté — connectez-le pour envoyer les baux depuis votre Gmail et classer les signés dans Drive
            </span>
          )}
          {isAdmin && (
            google.connected ? (
              <button onClick={disconnectGoogle} data-testid="google-disconnect-btn"
                className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors">
                <Unlink className="w-3.5 h-3.5" />Déconnecter
              </button>
            ) : (
              <Button size="sm" onClick={connectGoogle} data-testid="google-connect-btn"
                      className="ml-auto bg-blue-600 hover:bg-blue-700 text-white">
                Connecter Google
              </Button>
            )
          )}
        </div>
      )}

      <div className="flex gap-1.5 mb-6 bg-white border border-slate-200 rounded-xl p-1 w-fit">
        <button onClick={() => setTab("consulter")} data-testid="bm-tab-consulter"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "consulter" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Consulter
        </button>
        <button onClick={() => setTab("generer")} data-testid="bm-tab-generer"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "generer" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Générer
        </button>
      </div>

      {tab === "consulter" && (
        <>
          <div className="flex gap-1.5 mb-5">
            {[["actifs", "En cours"], ["classes", "Classés"], ["tous", "Tous"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`lease-filter-${k}`}
                className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                  filter === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}>{l}</button>
            ))}
          </div>
          {filter === "classes" && shown.length > 0 ? (
            <FiledList leases={shown} />
          ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {shown.map((l) => (
              <LeaseCard key={l.id} lease={l} onRefresh={load} canEdit={canEdit}
                         onResume={(lease) => setFlow({ open: true, lease, apartment: apts.find((a) => a.id === lease.apartment_id) })} />
            ))}
            {shown.length === 0 && (
              <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
                {filter === "classes" ? "Aucun bail classé" : "Aucun bail en cours — passez à l'onglet Générer pour démarrer"}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {tab === "generer" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {mobilityApts.map((a) => {
            const draft = leases.find((l) => l.apartment_id === a.id && l.status === "brouillon");
            const noDrive = !a.drive_folder_id;
            return (
              <div key={a.id} onClick={() => canEdit && !noDrive && openFlowForApartment(a)}
                      data-testid={`bm-apt-${a.id}`}
                className={`text-left bg-white rounded-2xl border p-4 shadow-sm transition-all ${noDrive ? "border-red-300 opacity-80" : "cursor-pointer hover:shadow-md border-slate-200 hover:border-blue-300"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                    <div className="text-xs text-slate-500 truncate">{a.address} · {a.arrondissement}</div>
                  </div>
                  {draft && <Badge className="bg-red-50 text-red-700 border-red-200 border flex-shrink-0">En cours</Badge>}
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  {noDrive ? (
                    driveEdit?.id === a.id ? (
                      <span className="flex items-center gap-1.5 flex-1" onClick={(e) => e.stopPropagation()}>
                        <Input value={driveEdit.value}
                               onChange={(e) => setDriveEdit({ id: a.id, value: e.target.value })}
                               placeholder="Collez le lien du dossier Drive (ou son ID)"
                               className="h-8 text-xs" data-testid={`bm-drive-input-${a.id}`} />
                        <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => saveDriveInline(a)} disabled={!driveEdit.value.trim()}
                                data-testid={`bm-drive-save-${a.id}`}>OK</Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2"
                                onClick={() => setDriveEdit(null)} data-testid={`bm-drive-cancel-${a.id}`}>✕</Button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-red-600 font-medium" data-testid={`bm-no-drive-${a.id}`}>
                          <AlertCircle className="w-3.5 h-3.5" />ID Drive en attente
                        </span>
                        {canEdit && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={(e) => { e.stopPropagation(); setDriveEdit({ id: a.id, value: "" }); }}
                                  data-testid={`bm-drive-add-${a.id}`}>
                            Ajouter l'ID
                          </Button>
                        )}
                      </span>
                    )
                  ) : (
                    <span className="flex items-center gap-1 text-slate-400">
                      <FolderOpen className="w-3.5 h-3.5" />Drive configuré
                    </span>
                  )}
                  {canEdit && !noDrive && (
                    <span className="ml-auto text-blue-600 font-medium">{draft ? "Reprendre →" : "Nouveau bail →"}</span>
                  )}
                </div>
              </div>
            );
          })}
          {mobilityApts.length === 0 && (
            <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
              Aucun appartement en bail mobilité
            </div>
          )}
        </div>
      )}

      <LeaseFlow open={flow.open} lease={flow.lease} apartment={flow.apartment}
                 onClose={() => setFlow({ open: false, lease: null, apartment: null })}
                 onSaved={load} />
    </div>
  );
}
